import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Question } from '@core/models/question';
import { User } from '@core/models/user';
import type { QuestionTemplate } from '../ui/questions/question-template';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';

export class QuestionData {
  route: string;
  component?: QuestionTemplate;
  stepPage: boolean;
  showHeader?: boolean;
  showIconInButton?: boolean;
  nextButtonTitle: string;
  description?: { title: string; text: string };
  imagePath: string;
  step: number;
  label: string;
}

@Injectable({
  providedIn: 'root',
})
export class QuestionService {
  readonly questionIndex = signal(0);
  readonly questionList = signal<QuestionData[]>([]);

  readonly stepBase = signal([
    { label: 'Produktauswahl', current: false, done: false, step: 0 },
    { label: 'Stammdaten', current: false, done: false, step: 1 },
    { label: 'Geschäftskonto eröffnen', current: false, done: false, step: 2 },
  ]);

  readonly steps = computed(() => this.updateStepsForStepper(this.questionList()[this.questionIndex()]?.step ?? -1));

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.questionList.set(this.buildQuestionList());

    effect(() => {
      const updatedList = this.initJurPerson();
      if (updatedList.length !== this.questionList().length) {
        this.questionList.set(updatedList);
      }
    });
  }

  initJurPerson(): QuestionData[] {
    if (!this.auth.isJurPerson()) {
      return this.questionList().filter((item) => item.route !== 'dokumente');
    }
    return this.questionList();
  }

  setComponent(route: string, component: QuestionTemplate): void {
    if (this.shouldRedirectToBlankoDokumente(route)) {
      this.router.navigate(['/blanko-dokumente']);
      return;
    }

    const foundIndex = this.questionList().findIndex((item) => item.route === route);

    if (foundIndex !== -1) {
      this.questionIndex.set(foundIndex);
      this.updateQuestionComponent(foundIndex, component);
    }
  }

  getQuestionObjByRoute(route: string): QuestionData | undefined {
    return this.questionList().find((q) => q.route === route);
  }

  getQuestionIndexByRoute(route: string): number {
    if (route === '') throw Error('empty route');
    return this.questionList().findIndex((q) => q.route === route.trim());
  }

  getQuestionObj(index: number): QuestionData {
    this.initJurPerson();
    if (index < this.questionList().length) {
      return this.questionList()[index];
    } else throw Error();
  }

  getComponent(index: number): QuestionTemplate | undefined {
    return this.questionList()[index]?.component;
  }

  getRouteName(index: number): string | null {
    if (index < this.questionList().length) return this.questionList()[index]?.route;
    else return null;
  }

  saveQuestions(userId: string, userquestions: Question[]) {
    return this.api.post<User>('user/questionaire', { userid: userId, userquestions });
  }

  getLastQuestion(userQuestionList: Question[]): QuestionData {
    const list = this.questionList();
    let last = list[0];
    list.forEach((_element, index) => {
      for (const question of userQuestionList) {
        if (question.name === list[index].route) {
          last = list[index + 1] ?? last;
        }
      }
    });
    return last;
  }

  nextStep(): void {
    const currentPath = this.getCurrentPath();
    this.questionIndex.set(this.getQuestionIndexByRoute(currentPath));
    const component = this.getComponent(this.questionIndex());
    const nextRoute = this.getRouteName(this.questionIndex() + 1);
    // not allow spring questions on double click
    if (nextRoute && component && (!component.answerFormGroup || component.answerFormGroup.valid)) {
      this.router.navigate([nextRoute], { relativeTo: this.route });
    }
  }

  getCurrentPath(): string {
    const [path] = this.router.url.split(/[?#]/);
    return path.split('/')[1] ?? '';
  }

  getNextButtonTitle(): string {
    const currentPath = this.getCurrentPath();
    return this.questionList()[this.getQuestionIndexByRoute(currentPath)]?.nextButtonTitle ?? '';
  }

  private buildQuestionList(): QuestionData[] {
    return [
      {
        route: 'overview',
        stepPage: false,
        showHeader: false,
        showIconInButton: false,
        nextButtonTitle: '',
        imagePath: 'assets/img/overview-mainImage',
        label: 'Willkommen',
        step: -1,
      },
      {
        route: 'bank-wahl',
        stepPage: true,
        showHeader: true,
        showIconInButton: true,
        nextButtonTitle: 'Nächster Schritt – Bestätigung Stammdaten',
        imagePath: 'assets/img/overview-mainImage',
        description: {
          title: '',
          text: this.translate.instant('PRODUKTAUSWAHL.INFO_TEXT'),
        },
        step: 0,
        label: 'Produktauswahl',
      },
      {
        route: 'zusammenfassung',
        stepPage: true,
        showHeader: true,
        showIconInButton: true,
        nextButtonTitle: 'Nächster Schritt - Rechtliche Angaben',
        imagePath: 'assets/img/stammdaten',
        step: 1,
        label: 'Stammdaten',
      },
      {
        route: 'dokumente',
        stepPage: true,
        showHeader: true,
        showIconInButton: true,
        nextButtonTitle: 'Nächster Schritt - Rechtliche Angaben',
        imagePath: 'assets/img/stammdaten',
        step: 1,
        label: 'Stammdaten & Rechtliches',
      },
      {
        route: 'blanko-dokumente',
        stepPage: true,
        showHeader: true,
        showIconInButton: true,
        nextButtonTitle: 'Nächster Schritt - Geschäftskonto eröffnen',
        imagePath: 'assets/img/rechtliches',
        step: 1,
        label: 'Dokumente',
      },
      {
        route: 'einsicht',
        stepPage: true,
        showHeader: true,
        showIconInButton: false,
        nextButtonTitle: 'Jetzt Geschäftskonto eröffnen',
        imagePath: 'assets/img/abschluss',
        step: 2,
        label: 'Geschäftskonto eröffnen',
      },
      {
        route: 'identifizierung',
        stepPage: false,
        showHeader: true,
        showIconInButton: false,
        nextButtonTitle: 'Gleich weiter zum Dashboard',
        imagePath: 'assets/img/abschluss',
        step: 3,
        label: 'Geschäftskonto eröffnen',
      },
    ];
  }

  private shouldRedirectToBlankoDokumente(route: string): boolean {
    return !this.auth.isJurPerson() && route === 'dokumente';
  }

  private updateQuestionComponent(index: number, component: QuestionTemplate): void {
    const updatedQuestions = [...this.questionList()];
    updatedQuestions[index] = { ...updatedQuestions[index], component };
    this.questionList.set(updatedQuestions);
  }

  private updateStepsForStepper(currentStep: number) {
    const newSteps = structuredClone(this.stepBase());
    for (const step of newSteps) {
      if (currentStep === -1) {
        step.current = false;
        step.done = false;
      } else if (step.step < currentStep) {
        step.done = true;
        step.current = false;
      } else if (step.step === currentStep) {
        step.current = true;
      }
    }
    return newSteps;
  }
}
